import { useQuery } from '@tanstack/react-query';

import APIGateway, {
  type ActionLogResponse, type ModelLogResponse, type RequestLogResponse, type ResponseLogResponse
} from '@/api';

export const logsKeys = {
  all: [ 'logs' ] as const,
  action: ( agentName: string, batchSize: number, offset: number ) => [
    ...logsKeys.all, 'action', agentName, batchSize, offset
  ] as const,
  model: ( agentName: string, batchSize: number, offset: number ) => [
    ...logsKeys.all, 'model', agentName, batchSize, offset
  ] as const,
  request: ( agentName: string, batchSize: number, offset: number, requestId?: string, correlationId?: string, sessionId?: string ) => [
    ...logsKeys.all, 'request', agentName, batchSize, offset, requestId, correlationId, sessionId
  ] as const,
  response: ( callId: string ) => [
    ...logsKeys.all, 'response', callId
  ] as const
};

/**
 * Hook to fetch action logs for an agent
 */
export function useActionLogsQuery(
  agentName: string | undefined,
  batchSize: number = 10,
  offset: number = 0
) {
  return useQuery( {
    queryKey: logsKeys.action( agentName!, batchSize, offset ),
    queryFn: async(): Promise<ActionLogResponse> => {
      if ( !agentName ) throw new Error( 'Agent name required' );

      const response = await APIGateway.logs.actionLogsGet( {
        agent_id: agentName,
        batch_size: batchSize,
        offset
      } );
      return response;
    },
    enabled: !!agentName,
    // Keep logs data fresh
    staleTime: 30000, // 30 seconds
    retry: 1 // Only retry once on failure
  } );
}

/**
 * Hook to fetch model logs for an agent
 */
export function useModelLogsQuery(
  agentName: string | undefined,
  batchSize: number = 10,
  offset: number = 0
) {
  return useQuery( {
    queryKey: logsKeys.model( agentName!, batchSize, offset ),
    queryFn: async(): Promise<ModelLogResponse> => {
      if ( !agentName ) throw new Error( 'Agent name required' );

      const response = await APIGateway.logs.modelLogsGet( {
        agent_id: agentName,
        batch_size: batchSize,
        offset
      } );
      return response;
    },
    enabled: !!agentName,
    staleTime: 30000,
    retry: 1
  } );
}

/**
 * Hook to fetch completion request logs
 */
export function useRequestLogsQuery(
  agentName: string | undefined,
  batchSize: number = 10,
  offset: number = 0,
  requestId?: string,
  correlationId?: string,
  sessionId?: string
) {
  return useQuery( {
    queryKey: logsKeys.request( agentName!, batchSize, offset, requestId, correlationId, sessionId ),
    queryFn: async(): Promise<RequestLogResponse> => {
      if ( !agentName ) throw new Error( 'Agent name required' );

      return APIGateway.logs.requestLogsGet( {
        agent_id: agentName,
        batch_size: batchSize,
        offset,
        request_id: requestId,
        correlation_id: correlationId,
        session_id: sessionId
      } );
    },
    enabled: !!agentName,
    staleTime: 30000,
    retry: 1
  } );
}

/**
 * Hook to fetch completion response logs for a given call ID
 */
export function useResponseLogsQuery( callId: string | null ) {
  return useQuery( {
    queryKey: logsKeys.response( callId! ),
    queryFn: async(): Promise<ResponseLogResponse> => {
      return APIGateway.logs.responseLogsGet( callId! );
    },
    enabled: !!callId,
    staleTime: 30000,
    retry: 1
  } );
}
